import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/hooks/useToast';
import { useAuthStore } from '@/store/authStore';
import type { 
  DeliveryChallan, 
  DeliveryChallanLine,
  CreateDCRequest,
  UpdateDCRequest,
  DCStatus,
  DCDispatchSummary 
} from '@/types/finishedGoods';

export function useDeliveryChallanFG() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deliveryChallans, setDeliveryChallans] = useState<DeliveryChallan[]>([]);
  const { toast } = useToast();
  const user = useAuthStore((state) => state.user);

  // Fetch all delivery challans
  const fetchDeliveryChallans = useCallback(async (filters?: {
    dc_type?: string;
    status?: DCStatus;
    work_order_id?: string;
    project_id?: string;
    customer_id?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<DeliveryChallan[]> => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('delivery_challan')
        .select(`
          *,
          lines:delivery_challan_line(*)
        `)
        .order('created_at', { ascending: false });

      if (filters?.dc_type) {
        query = query.eq('dc_type', filters.dc_type);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.work_order_id) {
        query = query.eq('work_order_id', filters.work_order_id);
      }
      if (filters?.project_id) {
        query = query.eq('project_id', filters.project_id);
      }
      if (filters?.customer_id) {
        query = query.eq('customer_id', filters.customer_id);
      }
      if (filters?.from_date) {
        query = query.gte('dc_date', filters.from_date);
      }
      if (filters?.to_date) {
        query = query.lte('dc_date', filters.to_date);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      
      setDeliveryChallans(data || []);
      return data || [];
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch delivery challans';
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Get DC by ID
  const getDeliveryChallan = useCallback(async (id: string): Promise<DeliveryChallan | null> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('delivery_challan')
        .select(`
          *,
          lines:delivery_challan_line(*)
        `)
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch delivery challan';
      setError(errorMessage);
      return null;
    }
  }, []);

  // Get DC lines
  const getDCLines = useCallback(async (dcId: string): Promise<DeliveryChallanLine[]> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('delivery_challan_line')
        .select('*')
        .eq('dc_id', dcId)
        .order('line_number');

      if (fetchError) throw fetchError;
      return data || [];
    } catch (err) {
      console.error('Failed to fetch DC lines:', err);
      return [];
    }
  }, []);

  // Create delivery challan
  const createDeliveryChallan = useCallback(async (request: CreateDCRequest): Promise<DeliveryChallan | null> => {
    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'You must be logged in to create delivery challans',
        variant: 'destructive',
      });
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      // Generate DC number
      const dcType = request.dc_type || 'FINISHED_GOODS';
      const { data: dcNumber, error: numError } = await supabase
        .rpc('generate_dc_number', { dc_type_param: dcType === 'SUBCONTRACT' ? 'SUBCONTRACT' : 'FG' });

      if (numError) throw numError;

      // Calculate total qty from lines
      const totalQty = request.lines.reduce((sum, line) => sum + line.quantity, 0);

      // Create the DC
      const { data: dc, error: insertError } = await supabase
        .from('delivery_challan')
        .insert({
          dc_number: dcNumber,
          dc_date: request.dc_date || new Date().toISOString(),
          dc_type: dcType,
          work_order_id: request.work_order_id,
          project_id: request.project_id,
          sales_order_id: request.sales_order_id,
          customer_id: request.customer_id,
          customer_name: request.customer_name,
          customer_address: request.customer_address,
          customer_gstin: request.customer_gstin,
          dispatch_from_warehouse_id: request.dispatch_from_warehouse_id,
          transporter_name: request.transporter_name,
          vehicle_number: request.vehicle_number,
          lr_number: request.lr_number,
          lr_date: request.lr_date,
          eway_bill_number: request.eway_bill_number,
          total_qty: totalQty,
          total_packages: request.total_packages,
          gross_weight: request.gross_weight,
          net_weight: request.net_weight,
          remarks: request.remarks,
          status: 'DRAFT',
          created_by: user.id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Create DC lines
      if (request.lines.length > 0) {
        const lines = request.lines.map((line, index) => ({
          dc_id: dc.id,
          line_number: index + 1,
          item_id: line.item_id,
          item_code: line.item_code,
          item_name: line.item_name,
          item_description: line.item_description,
          hsn_code: line.hsn_code,
          quantity: line.quantity,
          uom: line.uom || 'NOS',
          fg_receipt_id: line.fg_receipt_id,
          work_order_id: line.work_order_id,
          warehouse_id: line.warehouse_id || request.dispatch_from_warehouse_id,
          bin_id: line.bin_id,
          gross_weight: line.gross_weight,
          net_weight: line.net_weight,
          remarks: line.remarks,
        }));

        const { error: linesError } = await supabase
          .from('delivery_challan_line')
          .insert(lines);

        if (linesError) throw linesError;
      }

      toast({
        title: 'Success',
        description: `Delivery Challan ${dcNumber} created successfully`,
      });

      await fetchDeliveryChallans();
      return dc;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create delivery challan';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [user, toast, fetchDeliveryChallans]);

  // Update delivery challan
  const updateDeliveryChallan = useCallback(async (id: string, request: UpdateDCRequest): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const { error: updateError } = await supabase
        .from('delivery_challan')
        .update({
          ...request,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Delivery challan updated successfully',
      });

      await fetchDeliveryChallans();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update delivery challan';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, fetchDeliveryChallans]);

  // Confirm DC (change status to CONFIRMED)
  const confirmDC = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const { error: updateError } = await supabase
        .from('delivery_challan')
        .update({
          status: 'CONFIRMED',
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('status', 'DRAFT');

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Delivery challan confirmed',
      });

      await fetchDeliveryChallans();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to confirm delivery challan';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, fetchDeliveryChallans]);

  // Dispatch DC (change status to DISPATCHED and create inventory transactions)
  const dispatchDC = useCallback(async (id: string): Promise<boolean> => {
    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'You must be logged in to dispatch',
        variant: 'destructive',
      });
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      // Get DC with lines
      const dc = await getDeliveryChallan(id);
      if (!dc) throw new Error('Delivery challan not found');

      if (dc.status !== 'CONFIRMED') {
        throw new Error('Only confirmed delivery challans can be dispatched');
      }

      // Create inventory transactions for each line (stock OUT from FG warehouse)
      if (dc.lines && dc.lines.length > 0) {
        for (const line of dc.lines) {
          const { error: txError } = await supabase
            .from('inventory_tx')
            .insert({
              txn_type: 'DC_DISPATCH',
              item_id: line.item_id,
              qty: -line.quantity, // Negative for stock out
              uom: line.uom,
              wh_from: line.warehouse_id || dc.dispatch_from_warehouse_id,
              bin_from: line.bin_id,
              ref_table: 'delivery_challan_line',
              ref_id: line.id,
              note: `Dispatched via DC ${dc.dc_number}`,
              created_by: user.id,
            });

          if (txError) {
            console.error('Failed to create inventory transaction for line:', txError);
          }
        }
      }

      // Update DC status
      const { error: updateError } = await supabase
        .from('delivery_challan')
        .update({
          status: 'DISPATCHED',
          dispatched_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: `Delivery Challan ${dc.dc_number} dispatched successfully`,
      });

      await fetchDeliveryChallans();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to dispatch delivery challan';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, toast, getDeliveryChallan, fetchDeliveryChallans]);

  // Mark as delivered
  const markDelivered = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const { error: updateError } = await supabase
        .from('delivery_challan')
        .update({
          status: 'DELIVERED',
          delivered_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('status', 'DISPATCHED');

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Delivery challan marked as delivered',
      });

      await fetchDeliveryChallans();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to mark as delivered';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, fetchDeliveryChallans]);

  // Cancel DC
  const cancelDC = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const { error: updateError } = await supabase
        .from('delivery_challan')
        .update({
          status: 'CANCELLED',
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .in('status', ['DRAFT', 'CONFIRMED']);

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Delivery challan cancelled',
      });

      await fetchDeliveryChallans();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to cancel delivery challan';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, fetchDeliveryChallans]);

  // Delete DC (only draft)
  const deleteDC = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('delivery_challan')
        .delete()
        .eq('id', id)
        .eq('status', 'DRAFT');

      if (deleteError) throw deleteError;

      toast({
        title: 'Success',
        description: 'Delivery challan deleted',
      });

      await fetchDeliveryChallans();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete delivery challan';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, fetchDeliveryChallans]);

  // Get dispatch summary
  const getDispatchSummary = useCallback(async (): Promise<DCDispatchSummary> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('delivery_challan')
        .select('status, total_qty');

      if (fetchError) throw fetchError;

      const summary: DCDispatchSummary = {
        total_dcs: data?.length || 0,
        draft_count: 0,
        confirmed_count: 0,
        dispatched_count: 0,
        delivered_count: 0,
        total_qty_dispatched: 0,
      };

      data?.forEach((dc) => {
        switch (dc.status) {
          case 'DRAFT':
            summary.draft_count++;
            break;
          case 'CONFIRMED':
            summary.confirmed_count++;
            break;
          case 'DISPATCHED':
            summary.dispatched_count++;
            summary.total_qty_dispatched += dc.total_qty || 0;
            break;
          case 'DELIVERED':
            summary.delivered_count++;
            summary.total_qty_dispatched += dc.total_qty || 0;
            break;
        }
      });

      return summary;
    } catch (err) {
      console.error('Failed to get dispatch summary:', err);
      return {
        total_dcs: 0,
        draft_count: 0,
        confirmed_count: 0,
        dispatched_count: 0,
        delivered_count: 0,
        total_qty_dispatched: 0,
      };
    }
  }, []);

  return {
    loading,
    error,
    deliveryChallans,
    fetchDeliveryChallans,
    getDeliveryChallan,
    getDCLines,
    createDeliveryChallan,
    updateDeliveryChallan,
    confirmDC,
    dispatchDC,
    markDelivered,
    cancelDC,
    deleteDC,
    getDispatchSummary,
  };
}
